import React from "react";
import moment from "moment";
import "moment/locale/es";
import { toCamelCase } from "../../hooks/toCameCase.tsx";

moment.locale("es");

export interface Holiday {
  id: number;
  approved: boolean;
  isRejected: boolean;
  isRequest: boolean;
  startDate: number;
  endDate: number;
  daysRequested: number;
  createdAt: string;
  approvedByVacationsResponsible?: boolean;
  partner?: {
    person?: {
      firstName?: string;
      lastName?: string;
      secondLastName?: string;
    };
  };
  reviewerBy?: {
    name?: string;
    lastName?: string;
  };
}

const SORTABLE_COLUMNS = {
  ID: "id",
  PARTNER: "partner",
  START_DATE: "startDate",
  END_DATE: "endDate",
  DAYS_REQUESTED: "daysRequested",
  STATUS: "status",
  REVIEWER: "reviewer",
} as const;

const formatDateRange = (startDate: number, endDate: number): string => {
  if (!startDate || !endDate) return "-";
  const start = moment.unix(startDate).format("DD/MM/YYYY");
  const end = moment.unix(endDate).format("DD/MM/YYYY");
  return `${start} - ${end}`;
};

const STATUS_STYLES: Record<string, React.CSSProperties> = {
  approved: { background: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0" },
  pending_hr: { background: "#fef9c3", color: "#a16207", border: "1px solid #fde68a" },
  rejected: { background: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca" },
  pending: { background: "#fef3c7", color: "#b45309", border: "1px solid #fde68a" },
};

const BADGE_BASE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.3rem",
  padding: "0.28rem 0.65rem",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.02em",
  whiteSpace: "nowrap",
};

const StatusBadge: React.FC<{ holiday: Holiday }> = ({ holiday }) => {
  const { approved, isRejected, approvedByVacationsResponsible } = holiday;
  if (approved && approvedByVacationsResponsible) {
    return <span style={{ ...BADGE_BASE, ...STATUS_STYLES.approved }}>✓ Aprobado</span>;
  }
  if (approved && !approvedByVacationsResponsible) {
    return <span style={{ ...BADGE_BASE, ...STATUS_STYLES.pending_hr }}>⏳ Pend. responsable</span>;
  }
  if (isRejected) {
    return <span style={{ ...BADGE_BASE, ...STATUS_STYLES.rejected }}>✕ Rechazado</span>;
  }
  return <span style={{ ...BADGE_BASE, ...STATUS_STYLES.pending }}>○ Pendiente</span>;
};

const thStyle: React.CSSProperties = {
  padding: "0.75rem 1rem",
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#6b7280",
  background: "#f8fafc",
  borderBottom: "2px solid #e5e7eb",
  cursor: "pointer",
  whiteSpace: "nowrap",
  textAlign: "center",
};

const tdStyle: React.CSSProperties = {
  padding: "0.65rem 1rem",
  fontSize: "13px",
  color: "#374151",
  borderBottom: "1px solid #f3f4f6",
  textAlign: "center",
  verticalAlign: "middle",
};

interface TableHolidaysProps {
  holidays: Holiday[];
  sortBy: string;
  defaultSort: string;
  onSort: (column: string) => void;
  onReview: (holiday: Holiday) => void;
  error: string | null;
}

const COLUMNS: Array<{
  key: string;
  label: string;
  sortable: boolean;
  render: (holiday: Holiday) => React.ReactNode;
}> = [
  {
    key: SORTABLE_COLUMNS.ID,
    label: "Id",
    sortable: true,
    render: (h) => `${h.id}`,
  },
  {
    key: SORTABLE_COLUMNS.PARTNER,
    label: "Colaborador",
    sortable: true,
    render: (h) =>
      h.partner?.person
        ? toCamelCase(
            `${h.partner.person.firstName ?? ""} ${h.partner.person.lastName ?? ""} ${h.partner.person.secondLastName ?? ""}`
          )
        : "-",
  },
  {
    key: SORTABLE_COLUMNS.START_DATE,
    label: "Rango de fechas",
    sortable: true,
    render: (h) => formatDateRange(h.startDate, h.endDate),
  },
  {
    key: SORTABLE_COLUMNS.DAYS_REQUESTED,
    label: "Días solicitados",
    sortable: true,
    render: (h) => `${h.daysRequested ?? 0} días`,
  },
  {
    key: SORTABLE_COLUMNS.STATUS,
    label: "Estado",
    sortable: true,
    render: (h) => <StatusBadge holiday={h} />,
  },
  {
    key: SORTABLE_COLUMNS.REVIEWER,
    label: "Revisado por",
    sortable: true,
    render: (h) =>
      h.reviewerBy
        ? `${h.reviewerBy.name ?? ""} ${h.reviewerBy.lastName ?? ""}`.trim() || "-"
        : "-",
  },
  {
    key: "actions",
    label: "Acciones",
    sortable: false,
    render: () => null,
  },
];

const TableHolidays: React.FC<TableHolidaysProps> = ({
  holidays,
  sortBy,
  defaultSort,
  onSort,
  onReview,
  error,
}) => {
  const getIconSort = () => (
    <i className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"} ms-1`} style={{ fontSize: 10 }} />
  );

  return (
    <div className="mb-3" style={{ minHeight: "560px" }}>
      <div
        className="table-responsive"
        style={{
          minHeight: "560px",
          maxHeight: "700px",
          overflowY: "auto",
          overflowX: "auto",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
        }}
      >
        <table
          className="table table-panel text-nowrap align-middle mb-0"
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
        >
          <thead>
            <tr style={{ position: "sticky", top: 0, zIndex: 1 }}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    ...thStyle,
                    cursor: col.sortable ? "pointer" : "default",
                  }}
                  onClick={() => col.sortable && onSort(col.key)}
                >
                  {col.label}
                  {col.sortable && sortBy === col.key && getIconSort()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {holidays?.length > 0 ? (
              holidays.map((holiday, idx) => (
                <tr
                  key={holiday.id}
                  style={{
                    background: idx % 2 === 0 ? "#ffffff" : "#fafafa",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f9ff")}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : "#fafafa")
                  }
                >
                  {COLUMNS.map((col) => (
                    <td key={col.key} style={tdStyle}>
                      {col.key === "actions" ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "0.35rem",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => onReview(holiday)}
                            className="btn btn-azul btn-sm rounded-pill"
                            style={{ paddingInline: "1.1rem" }}
                          >
                            <i className="fa fa-eye me-1" style={{ fontSize: 11 }} />
                            Revisar
                          </button>
                        </div>
                      ) : (
                        col.render(holiday)
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={COLUMNS.length}>
                  <div style={{ padding: "3rem 1rem", textAlign: "center" }}>
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        background: "#f1f5f9",
                        margin: "0 auto 1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className="fa fa-umbrella-beach" style={{ fontSize: 24, color: "#94a3b8" }} />
                    </div>
                    {error ? (
                      <p style={{ color: "#ef4444", fontWeight: 600, margin: 0 }}>
                        <i className="fa fa-exclamation-triangle me-2" />
                        Error al cargar: {error}
                      </p>
                    ) : (
                      <>
                        <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 0.25rem" }}>
                          Sin vacaciones registradas
                        </p>
                        <p style={{ color: "#9ca3af", fontSize: 13, margin: 0 }}>
                          Las solicitudes de vacaciones aparecerán aquí
                        </p>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableHolidays;
